/**
 * Pure, unit-tested filter logic behind the offers list filter bar. Kept out
 * of the page component so the predicate + distinct-value + preset math is
 * testable in isolation (mirrors the catalogFilters.ts idiom).
 *
 * Client-side only: the full offers list is already loaded by the page
 * (server-side filtering is out of scope while volume stays low).
 *
 * Offers are filtered by `stageId` (configurable stages, see api/offerStages.ts)
 * and by a text `search` criterion — matched over customer/offerNumber/item
 * product names via offersBoard.ts's matchesSearch (same predicate the
 * kanban board and funnel use, kept in one place).
 */
import type { Offer } from "../api/offers";
import { matchesSearch, type BoardOffer } from "./offersBoard";

/** Slice of Offer the filter predicate + distinct-value helpers actually need. */
export type FilterableOffer = Pick<Offer, "customerCountry" | "offerDate" | "ownerUserId" | "ownerName"> & BoardOffer;

/** Sentinel country value standing in for offers with no customerCountry — grouped under "—" in the dropdown. */
export const EMPTY_COUNTRY = "__EMPTY__";

export type OfferFilterCriteria = {
  /** "" = all stages; otherwise an offer_stage id. */
  stageId: string;
  /** "" = all countries; EMPTY_COUNTRY = the grouped "no country" bucket. */
  country: string;
  /** Inclusive lower bound on offerDate, "YYYY-MM-DD"; "" = no lower bound. */
  dateFrom: string;
  /** Inclusive upper bound on offerDate, "YYYY-MM-DD"; "" = no upper bound. */
  dateTo: string;
  /** ownerUserId; "" = all owners. */
  ownerId: string;
  /** Free text over customer/offerNumber/item product names; "" = no filter. */
  search: string;
};

export const EMPTY_FILTER_CRITERIA: OfferFilterCriteria = {
  stageId: "",
  country: "",
  dateFrom: "",
  dateTo: "",
  ownerId: "",
  search: "",
};

/** AND-combines every active criterion; offerDate strings compare lexically (ISO dates sort correctly). */
export function filterOffers<T extends FilterableOffer>(offers: T[], criteria: OfferFilterCriteria): T[] {
  return offers.filter((o) => {
    if (criteria.stageId && o.stage?.id !== criteria.stageId) return false;
    if (criteria.country) {
      const country = o.customerCountry || EMPTY_COUNTRY;
      if (country !== criteria.country) return false;
    }
    if (criteria.dateFrom && o.offerDate < criteria.dateFrom) return false;
    if (criteria.dateTo && o.offerDate > criteria.dateTo) return false;
    if (criteria.ownerId && o.ownerUserId !== criteria.ownerId) return false;
    if (criteria.search && !matchesSearch(o, criteria.search)) return false;
    return true;
  });
}

/** True when at least one criterion is active — drives the "clear filters" button visibility. */
export function hasActiveFilter(criteria: OfferFilterCriteria): boolean {
  return (
    criteria.stageId !== "" ||
    criteria.country !== "" ||
    criteria.dateFrom !== "" ||
    criteria.dateTo !== "" ||
    criteria.ownerId !== "" ||
    criteria.search !== ""
  );
}

/** Distinct customerCountry codes present in the loaded list, sorted; blanks collapse to EMPTY_COUNTRY, listed last. */
export function distinctCountries(offers: FilterableOffer[]): string[] {
  const codes = new Set<string>();
  let hasEmpty = false;
  for (const o of offers) {
    if (o.customerCountry) codes.add(o.customerCountry);
    else hasEmpty = true;
  }
  const sorted = [...codes].sort((a, b) => a.localeCompare(b));
  return hasEmpty ? [...sorted, EMPTY_COUNTRY] : sorted;
}

export type OwnerOption = { id: string; name: string };

/** Distinct owners (ownerUserId + display name) present in the loaded list, sorted by name. */
export function distinctOwners(offers: FilterableOffer[]): OwnerOption[] {
  const byId = new Map<string, string>();
  for (const o of offers) {
    if (!byId.has(o.ownerUserId)) byId.set(o.ownerUserId, o.ownerName ?? "—");
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type DatePreset = "thisMonth" | "thisQuarter" | "thisYear" | "all";

const pad2 = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, monthIndex0: number, day: number) => `${y}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
const daysInMonth = (y: number, monthIndex0: number) => new Date(y, monthIndex0 + 1, 0).getDate();

/** Computes the inclusive {from, to} date-string range for a preset, relative to `now`. "all" clears the range. */
export function presetRange(preset: DatePreset, now: Date): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "thisMonth":
      return { from: iso(y, m, 1), to: iso(y, m, daysInMonth(y, m)) };
    case "thisQuarter": {
      const qStart = Math.floor(m / 3) * 3;
      const qEnd = qStart + 2;
      return { from: iso(y, qStart, 1), to: iso(y, qEnd, daysInMonth(y, qEnd)) };
    }
    case "thisYear":
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    case "all":
      return { from: "", to: "" };
  }
}
